import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import '../styles/Interchanges.css';
import { translations } from '../translations';

const INTERCHANGE_IMAGES = [
  { file: 'Old Madras Road Interchange 200226.jpg.jpeg', labelEn: 'Old Madras Road Interchange', labelKn: 'ಓಲ್ಡ್ ಮದ್ರಾಸ್ ರೋಡ್ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Channasandra Interchange 200226.jpg.jpeg', labelEn: 'Channasandra Interchange', labelKn: 'ಚನ್ನಸಂದ್ರ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Madanayakanhalli Interchange 200226.jpg.jpeg', labelEn: 'Madanayakanahalli Interchange', labelKn: 'ಮಾದನಾಯಕನಹಳ್ಳಿ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Bellary Road Interchange 200226.jpg.jpeg', labelEn: 'Bellary Road Interchange', labelKn: 'ಬಳ್ಳಾರಿ ರೋಡ್ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Hennur Road Interchange 200226.jpg.jpeg', labelEn: 'Hennur Road Interchange', labelKn: 'ಹೆಣ್ಣೂರು ರೋಡ್ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Whitefield Interchange 200226.jpg.jpeg', labelEn: 'Whitefield Interchange', labelKn: 'ವೈಟ್ ಫೀಲ್ಡ್ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Doddaballapura Interchange 200226.jpg.jpeg', labelEn: 'Doddaballapura Interchange', labelKn: 'ದೊಡ್ಡಬಳ್ಳಾಪುರ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Madavara Interchange 200226.jpg.jpeg', labelEn: 'Madavara Interchange', labelKn: 'ಮಾದಾವರ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Hesarghatta Grade Separator 200226.jpg.jpeg', labelEn: 'Hesarghatta Grade Separator', labelKn: 'ಹೆಸರಘಟ್ಟ ಗ್ರೇಡ್ ಸೆಪರೇಟರ್' },
  { file: 'Hosur Road Interchange 200226.jpg.jpeg', labelEn: 'Hosur Road Interchange', labelKn: 'ಹೊಸೂರು ರೋಡ್ ಇಂಟರ್‌ಚೇಂಜ್' },
  { file: 'Sarjapur Interchange 200226.jpg.jpeg', labelEn: 'Sarjapur Interchange', labelKn: 'ಸರ್ಜಾಪುರ ಇಂಟರ್‌ಚೇಂಜ್' }
];

const Interchanges = ({ language }) => {
  const t = translations[language];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const baseUrl = process.env.PUBLIC_URL || '';

  const carouselImages = useMemo(() =>
    INTERCHANGE_IMAGES.map(({ file, labelEn, labelKn }) => ({
      src: `${baseUrl}/interchanges/${file}`,
      label: language === 'kannada' ? labelKn : labelEn
    })),
    [baseUrl, language]
  );

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    setCurrentSlide(0);
    setIsPaused(false);
  }, [language]);

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [carouselImages.length, isPaused]);

  const goToPrev = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  }, [carouselImages.length]);

  const goToNext = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  }, [carouselImages.length]);

  const handleImageClick = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const lightboxPrev = useCallback((e) => {
    e.stopPropagation();
    setLightboxIndex(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, [carouselImages.length]);

  const lightboxNext = useCallback((e) => {
    e.stopPropagation();
    setLightboxIndex(prev => (prev + 1) % carouselImages.length);
  }, [carouselImages.length]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    const handleArrow = (e) => {
      if (e.key === 'ArrowLeft') lightboxPrev(e);
      if (e.key === 'ArrowRight') lightboxNext(e);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);
    window.addEventListener('keydown', handleArrow);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
      window.removeEventListener('keydown', handleArrow);
    };
  }, [lightboxOpen, closeLightbox, lightboxPrev, lightboxNext]);

  return (
    <section id="interchanges" className="interchanges-section">
      <div className="container">
        <div className="interchanges-content">
          <div className="section-header">
            <h2 className="interchanges-section-title">{t.interchanges?.title ?? 'Interchanges'}</h2>
          </div>

          <div className="ic-slider">
            <button
              type="button"
              className="ic-arrow ic-arrow-prev"
              onClick={goToPrev}
              aria-label={language === 'kannada' ? 'ಹಿಂದೆ' : 'Previous'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="ic-track">
              {carouselImages.map((image, index) => (
                <div
                  key={image.src}
                  className={`ic-slide ${index === currentSlide ? 'ic-slide-current' : ''}`}
                  onClick={(e) => handleImageClick(e, index)}
                >
                  <img
                    src={image.src}
                    alt={image.label}
                    className="ic-slide-img"
                    loading="lazy"
                  />
                  <div className="ic-slide-label">{image.label}</div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="ic-arrow ic-arrow-next"
              onClick={goToNext}
              aria-label={language === 'kannada' ? 'ಮುಂದೆ' : 'Next'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="ic-pagination">
            {carouselImages.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`ic-pagination-item ${index === currentSlide ? 'ic-pagination-item-active' : ''}`}
                onClick={() => {
                  setCurrentSlide(index);
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 5000);
                }}
                aria-label={`${language === 'kannada' ? 'ಸ್ಲೈಡ್' : 'Slide'} ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {lightboxOpen && mounted && createPortal(
        <div
          className="ic-lightbox-backdrop"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={language === 'kannada' ? 'ಚಿತ್ರ ವೀಕ್ಷಣೆ' : 'Image viewer'}
        >
          <button
            type="button"
            className="ic-lightbox-close"
            onClick={closeLightbox}
            aria-label={language === 'kannada' ? 'ಮುಚ್ಚಿ' : 'Close'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
          <div className="ic-lightbox-stage">
            <button
              type="button"
              className="ic-lightbox-arrow ic-lightbox-prev"
              onClick={lightboxPrev}
              aria-label={language === 'kannada' ? 'ಹಿಂದೆ' : 'Previous'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5" />
                <path d="M12 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="ic-lightbox-body" onClick={(e) => e.stopPropagation()}>
              <img
                src={carouselImages[lightboxIndex].src}
                alt={carouselImages[lightboxIndex].label}
                className="ic-lightbox-img"
              />
              <div className="ic-lightbox-caption">
                {carouselImages[lightboxIndex].label}
              </div>
              <div className="ic-lightbox-counter">
                {lightboxIndex + 1} / {carouselImages.length}
              </div>
            </div>
            <button
              type="button"
              className="ic-lightbox-arrow ic-lightbox-next"
              onClick={lightboxNext}
              aria-label={language === 'kannada' ? 'ಮುಂದೆ' : 'Next'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default Interchanges;
